import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { MMEMParams, Persona } from '../types';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import ControlsSidebar from '../components/dashboard/ControlsSidebar';
import MemoryDynamics from '../components/dashboard/MemoryDynamics';
import FormulaSection from '../components/dashboard/FormulaSection';
import DashboardFooter from '../components/dashboard/DashboardFooter';
import AboutModal from '../components/AboutModal';
import SEO from '../components/SEO';

export default function Dashboard({
  params,
  setParams,
  isDark,
  setIsDark,
}: {
  params: MMEMParams;
  setParams: React.Dispatch<React.SetStateAction<MMEMParams>>;
  isDark: boolean;
  setIsDark: (v: boolean) => void;
}) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const L = useMemo(() => {
    const { attention, mood, environment, repetition, spacing, sleep } = params;
    return attention * mood * environment + repetition * spacing + sleep;
  }, [params]);

  const decayData = useMemo(() => {
    const data = [];
    const timeRange = Math.max(100, L * 0.5);
    for (let t = 0; t <= timeRange; t += timeRange / 50) {
      const p = Math.exp(-t / Math.max(1, L));
      data.push({
        time: Math.round(t * 10) / 10,
        recall: Math.round(p * 100),
      });
    }
    return data;
  }, [L]);

  const sawtoothData = useMemo(() => {
    const data = [];
    let currentL =
      params.attention * params.mood * params.environment + params.sleep;
    let currentTime = 0;

    for (let i = 0; i < 5; i++) {
      const interval = params.spacing * 2;
      for (let step = 0; step <= interval; step += 0.5) {
        const t = currentTime + step;
        const decayFactor = Math.exp(-step / Math.max(1, currentL));
        data.push({
          time: Math.round(t * 10) / 10,
          strength: Math.round(currentL * decayFactor * 10) / 10,
        });
      }
      currentTime += interval;
      currentL += params.repetition * params.spacing;
    }
    return data;
  }, [params]);

  const triadRadarData = useMemo(
    () => [
      { subject: 'Attention', A: params.attention, fullMark: 10 },
      { subject: 'Mood', A: params.mood, fullMark: 10 },
      { subject: 'Environment', A: params.environment, fullMark: 10 },
    ],
    [params.attention, params.mood, params.environment]
  );

  const handleParamChange = (key: keyof MMEMParams, value: number) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const loadPersona = (persona: Persona) => {
    setParams(persona.params);
  };

  return (
    <div className="bg-background selection:bg-primary/20 selection:text-primary min-h-screen font-sans">
      <SEO
        title="Model Playground | MMEM Theory"
        description="Explore the mathematical framework of the Mojolagbe Memory Encoding Model (MMEM). Visualize memory decay and retention dynamics."
      />
      {/* Decorative Background Elements */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="bg-primary/10 absolute -top-[10%] -left-[10%] h-[40%] w-[40%] rounded-full blur-[120px]" />
        <div className="bg-secondary/10 absolute top-[20%] -right-[5%] h-[30%] w-[30%] rounded-full blur-[100px]" />
        <div className="bg-accent/10 absolute -bottom-[10%] left-[20%] h-[50%] w-[50%] rounded-full blur-[150px]" />

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] bg-[size:40px_40px]" />

        {/* Animated Neural Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                x: Math.random() * 100 + '%',
                y: Math.random() * 100 + '%',
                opacity: 0.1,
              }}
              animate={{
                y: [null, '-10%', '110%'],
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 10 + Math.random() * 20,
                repeat: Infinity,
                ease: 'linear',
                delay: i * 2,
              }}
              className="bg-primary absolute h-1 w-1 rounded-full opacity-40 blur-[1px]"
            />
          ))}
        </div>
      </div>

      <DashboardHeader
        isDark={isDark}
        setIsDark={setIsDark}
        L={L}
        onToggleFilters={() => setIsFiltersOpen(true)}
      />

      <main className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 py-8 lg:grid-cols-12">
        <ControlsSidebar
          params={params}
          handleParamChange={handleParamChange}
          loadPersona={loadPersona}
          isMobileOpen={isFiltersOpen}
          onMobileClose={() => setIsFiltersOpen(false)}
        />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 lg:col-span-8"
        >
          <MemoryDynamics
            isDark={isDark}
            L={L}
            params={params}
            decayData={decayData}
            sawtoothData={sawtoothData}
            triadRadarData={triadRadarData}
          />
          <FormulaSection />
        </motion.div>
      </main>

      <DashboardFooter onOpenAbout={() => setIsAboutOpen(true)} />

      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </div>
  );
}
